import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Upload, FilePlus2, Eye, Pencil, Trash2, GitBranch, Loader2, Plus, AlertTriangle, Printer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, openBlob } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge, ConfirmDialog, Empty, Field, NativeSelect } from "../common";
import { eur2 as eur, fmtDate } from "../../lib/format";
import { cn } from "../../lib/utils";
import { QUOTE_KIND } from "./config";

let seq = 0;
const withUid = (r) => ({ ...r, uid: `row-${++seq}` });
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const sumItems = (items) => round2(items.reduce((s, i) => s + (Number(i.amount) || 0), 0));
const emptyRow = () => withUid({ description: "", unit: "", quantity: 1, unit_price: 0, amount: 0 });
const itemPayload = (i) => ({ description: i.description, unit: i.unit, quantity: Number(i.quantity), unit_price: Number(i.unit_price), amount: Number(i.amount) });
const installmentPayload = (r) => ({ description: r.description, pct: Number(r.pct), kind: r.kind });
const quoteMeta = (q) => [q.number && `n. ${q.number}`, q.client, q.quote_date && fmtDate(q.quote_date),
  `${q.items.length} ${q.items.length === 1 ? "riga" : "righe"}`].filter(Boolean).join(" · ");

const AMOUNT_KEYS = ["quantity", "unit_price", "amount"];

function ItemsTable({ items, setItems }) {
  const update = (idx, key, val) => setItems((list) => list.map((r, i) => {
    if (i !== idx) return r;
    const next = { ...r, [key]: val };
    if (key === "quantity" || key === "unit_price") next.amount = round2(Number(next.quantity) * Number(next.unit_price));
    return next;
  }), AMOUNT_KEYS.includes(key));
  const cell = "h-8 px-2 text-sm";
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm min-w-[720px]" data-testid="quote-items-table">
        <thead><tr className="text-left text-xs text-muted-foreground"><th className="p-1">Descrizione lavorazione</th><th className="p-1 w-20">U.M.</th><th className="p-1 w-24">Quantità</th><th className="p-1 w-28">Prezzo unit. €</th><th className="p-1 w-28">Importo €</th><th className="w-8" /></tr></thead>
        <tbody>
          {items.map((r, i) => (
            <tr key={r.uid} className={cn(r.check && "bg-amber-950/40")} data-testid={`quote-item-${i}`}>
              <td className="p-1"><Input className={cell} value={r.description} onChange={(e) => update(i, "description", e.target.value)} data-testid={`quote-item-desc-${i}`} /></td>
              <td className="p-1"><Input className={cell} value={r.unit || ""} onChange={(e) => update(i, "unit", e.target.value)} /></td>
              <td className="p-1"><Input className={cell} type="number" step="0.01" value={r.quantity} onChange={(e) => update(i, "quantity", e.target.value)} data-testid={`quote-item-qty-${i}`} /></td>
              <td className="p-1"><Input className={cell} type="number" step="0.01" value={r.unit_price} onChange={(e) => update(i, "unit_price", e.target.value)} data-testid={`quote-item-price-${i}`} /></td>
              <td className="p-1"><Input className={cell} type="number" step="0.01" value={r.amount} onChange={(e) => update(i, "amount", e.target.value)} data-testid={`quote-item-amount-${i}`} /></td>
              <td><Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => setItems((l) => l.filter((x) => x.uid !== r.uid))} data-testid={`quote-item-remove-${i}`}><Trash2 className="h-4 w-4" /></Button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button size="sm" variant="outline" className="mt-2" onClick={() => setItems((l) => [...l, emptyRow()])} data-testid="quote-item-add-btn"><Plus className="h-4 w-4 mr-1" />Aggiungi riga</Button>
    </div>
  );
}

function initialQuote(draft) {
  return { kind: "preventivo", title: "", number: "", client: "", quote_date: "", notes: "", total: 0, ...draft, items: draft.items || [] };
}

function InstallmentsBox({ plan, setPlan, total }) {
  const update = (idx, key, val) => setPlan((p) => ({ ...p, rows: p.rows.map((r, i) => (i === idx ? { ...r, [key]: val } : r)) }));
  const pctSum = plan.rows.reduce((s, r) => s + (Number(r.pct) || 0), 0);
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid="quote-installments">
      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={plan.enabled} onChange={(e) => setPlan((p) => ({ ...p, enabled: e.target.checked }))} data-testid="quote-installments-toggle" />
        Crea gli importi da incassare dal cliente (calcolati sull'imponibile)
      </label>
      {plan.enabled && plan.rows.map((r, i) => (
        <div key={r.uid} className="grid grid-cols-[1fr_90px_120px_32px] gap-2 items-center" data-testid={`quote-installment-${i}`}>
          <Input className="h-8 text-sm" value={r.description} onChange={(e) => update(i, "description", e.target.value)} data-testid={`quote-installment-desc-${i}`} />
          <Input className="h-8 text-sm" type="number" step="0.01" value={r.pct} onChange={(e) => update(i, "pct", e.target.value)} data-testid={`quote-installment-pct-${i}`} />
          <span className="num text-sm text-right">{eur(round2(total * (Number(r.pct) || 0) / 100))}</span>
          <Button size="icon" variant="ghost" className="h-8 w-8 text-red-400" onClick={() => setPlan((p) => ({ ...p, rows: p.rows.filter((x) => x.uid !== r.uid) }))} data-testid={`quote-installment-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      {plan.enabled && (
        <div className="flex items-center justify-between text-xs">
          <Button size="sm" variant="outline" onClick={() => setPlan((p) => ({ ...p, rows: [...p.rows, withUid({ description: "Saldo fine lavori", pct: Math.max(0, 100 - pctSum), kind: "saldo" })] }))} data-testid="quote-installment-add-btn"><Plus className="h-4 w-4 mr-1" />Aggiungi rata</Button>
          <span className={cn("num", pctSum > 100 && "text-red-400")}>Totale rate: {round2(pctSum)}%</span>
        </div>
      )}
    </div>
  );
}

const DEFAULT_PLAN = [{ description: "50% all'ordine", pct: 50, kind: "acconto" }, { description: "50% fine lavori", pct: 50, kind: "saldo" }];

function initialPlan(editor) {
  const rows = editor.draft.installments?.length ? editor.draft.installments : DEFAULT_PLAN;
  return { enabled: Boolean(editor.draft.installments?.length) && !editor.quoteId && !editor.replacesId, rows: rows.map(withUid) };
}

function useQuoteEditor({ siteId, editor, onClose, onSaved }) {
  const [q, setQ] = useState(() => initialQuote(editor.draft));
  const [items, setItems] = useState(() => q.items.map(withUid));
  const [itemsTouched, setItemsTouched] = useState(false);
  const [plan, setPlan] = useState(() => initialPlan(editor));
  const [busy, setBusy] = useState(false);
  const itemsSum = useMemo(() => sumItems(items), [items]);
  const editItems = useCallback((fn, affectsTotal = true) => {
    if (affectsTotal) {
      setItemsTouched(true);
    }
    setItems(fn);
  }, []);
  useEffect(() => {
    if (itemsTouched) {
      setQ((x) => ({ ...x, total: itemsSum }));
    }
  }, [itemsTouched, itemsSum]);
  const set = (k) => (e) => setQ((x) => ({ ...x, [k]: e.target.value }));
  const save = async () => {
    if (!q.title.trim()) return toast.error("Inserisci un titolo / oggetto");
    setBusy(true);
    const installments = plan.enabled && !editor.quoteId ? plan.rows.map(installmentPayload) : [];
    const body = { kind: q.kind, title: q.title, number: q.number, client: q.client, notes: q.notes, ...editor.file,
      items: items.map(itemPayload), total: Number(q.total), quote_date: q.quote_date || null, replaces_id: editor.replacesId || null, installments };
    try {
      if (editor.quoteId) await api.put(`/finance/quotes/${editor.quoteId}`, body);
      else await api.post(`/finance/sites/${siteId}/quotes`, body);
      toast.success("✓ Preventivo registrato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return { q, setQ, set, items, setItems: editItems, plan, setPlan, busy, save, itemsSum };
}

function QuoteHeaderFields({ q, set }) {
  return (
    <div className="grid sm:grid-cols-3 gap-3">
      <Field label="Tipo"><NativeSelect value={q.kind} onChange={set("kind")} data-testid="quote-kind">{Object.entries(QUOTE_KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</NativeSelect></Field>
      <Field label="Titolo / oggetto" className="sm:col-span-2"><Input value={q.title} onChange={set("title")} data-testid="quote-title" /></Field>
      <Field label="Cliente"><Input value={q.client || ""} onChange={set("client")} data-testid="quote-client" /></Field>
      <Field label="Numero"><Input value={q.number || ""} onChange={set("number")} data-testid="quote-number" /></Field>
      <Field label="Data"><Input type="date" value={q.quote_date || ""} onChange={set("quote_date")} data-testid="quote-date" /></Field>
    </div>
  );
}

function QuoteNotesTotal({ q, set, setQ, itemsSum }) {
  return (
    <div className="grid sm:grid-cols-[1fr_220px] gap-3 items-end">
      <Field label="Note"><Textarea rows={2} value={q.notes || ""} onChange={set("notes")} data-testid="quote-notes" /></Field>
      <Field label={`Totale € (somma righe ${eur(itemsSum)})`}>
        <div className="flex gap-1"><Input type="number" step="0.01" value={q.total} onChange={set("total")} data-testid="quote-total" />
          <Button variant="outline" size="sm" className="h-10" onClick={() => setQ((x) => ({ ...x, total: itemsSum }))} data-testid="quote-use-sum-btn">=Σ</Button></div>
      </Field>
    </div>
  );
}

const Warnings = ({ list }) => (list || []).map((w) => (
  <div key={w} className="flex gap-2 text-sm text-amber-200 bg-amber-950/50 border border-amber-700 rounded-md p-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />{w}</div>
));

function QuoteEditor(props) {
  const { editor, onClose } = props;
  const e = useQuoteEditor(props);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-popover max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="quote-editor">
        <DialogHeader><DialogTitle>{editor.replacesId ? "Nuova revisione" : "Verifica e conferma dati"}</DialogTitle></DialogHeader>
        <Warnings list={editor.draft.warnings} />
        {editor.file?.file_name && <div className="text-xs text-muted-foreground">File: {editor.file.file_name}</div>}
        <QuoteHeaderFields q={e.q} set={e.set} />
        <ItemsTable items={e.items} setItems={e.setItems} />
        <QuoteNotesTotal q={e.q} set={e.set} setQ={e.setQ} itemsSum={e.itemsSum} />
        {!editor.quoteId && <InstallmentsBox plan={e.plan} setPlan={e.setPlan} total={Number(e.q.total) || 0} />}
        <Button onClick={e.save} disabled={e.busy} data-testid="quote-save-btn">{e.busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Conferma e registra</Button>
      </DialogContent>
    </Dialog>
  );
}

const quoteDraft = (q) => ({ kind: q.kind, title: q.title, number: q.number, client: q.client, quote_date: q.quote_date, notes: q.notes, total: q.total, items: q.items });

function QuoteRow({ q, onOpen, onEdit, onRevise, onDelete }) {
  const old = q.status === "sostituito";
  return (
    <li className={cn("flex flex-col sm:flex-row sm:items-center gap-2 py-3", old && "opacity-60")} data-testid={`quote-row-${q.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Badge className="bg-sky-950 border-sky-600 text-sky-200">{QUOTE_KIND[q.kind]}</Badge><span className="font-medium">{q.title}</span>
          <Badge className={old ? "bg-slate-800 border-slate-600 text-slate-300" : "bg-emerald-950 border-emerald-600 text-emerald-200"}>rev. {q.revision} · {old ? "sostituita" : "in vigore"}</Badge></div>
        <div className="text-xs text-muted-foreground mt-1">{quoteMeta(q)}</div>
      </div>
      <div className="num font-semibold">{eur(q.total)}</div>
      <div className="flex gap-1">
        {q.has_file && <Button size="icon" variant="ghost" onClick={() => onOpen(q)} data-testid={`quote-file-${q.id}`} aria-label="Apri file"><Eye className="h-4 w-4" /></Button>}
        <Button size="icon" variant="ghost" onClick={() => window.open(`/stampa-lavorazioni/${q.id}`, "_blank")} data-testid={`quote-print-work-${q.id}`} aria-label="Stampa senza importi" title="Stampa senza importi (per operai e fornitori)"><Printer className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onEdit(q)} data-testid={`quote-edit-${q.id}`} aria-label="Modifica"><Pencil className="h-4 w-4" /></Button>
        {!old && <Button size="icon" variant="ghost" onClick={() => onRevise(q)} data-testid={`quote-revise-${q.id}`} aria-label="Nuova revisione"><GitBranch className="h-4 w-4" /></Button>}
        <Button size="icon" variant="ghost" className="text-red-400" onClick={() => onDelete(q)} data-testid={`quote-delete-${q.id}`} aria-label="Elimina"><Trash2 className="h-4 w-4" /></Button>
      </div>
    </li>
  );
}

function useQuoteUpload(setEditor) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/finance/quotes/parse", fd);
      setEditor({ draft: data.draft, file: data.file });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { busy, upload };
}

export default function QuotesTab({ siteId, quotes, reload }) {
  const [editor, setEditor] = useState(null);
  const [del, setDel] = useState(null);
  const inputRef = useRef(null);
  const { busy, upload } = useQuoteUpload(setEditor);
  const remove = async () => {
    try {
      await api.delete(`/finance/quotes/${del.id}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setDel(null);
  };
  return (
    <section className="surface p-5" data-testid="fin-tab-quotes">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="font-semibold">Preventivi, capitolati e storico revisioni</h3>
        <div className="flex flex-wrap gap-2">
          <input ref={inputRef} type="file" accept=".pdf,image/*" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="quote-upload-input" />
          <Button onClick={() => inputRef.current.click()} disabled={busy} data-testid="quote-upload-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}Carica PDF e leggi dati</Button>
          <Button variant="outline" onClick={() => setEditor({ draft: {} })} data-testid="quote-manual-btn"><FilePlus2 className="h-4 w-4 mr-2" />Inserisci a mano</Button>
        </div>
      </div>
      {!quotes.length && <Empty title="Nessun preventivo registrato" />}
      <ul className="divide-y divide-border/60">
        {quotes.map((q) => (
          <QuoteRow key={q.id} q={q} onDelete={setDel}
            onOpen={(x) => openBlob(`/finance/quotes/${x.id}/file`).catch((e) => toast.error(errMsg(e)))}
            onEdit={(x) => setEditor({ draft: quoteDraft(x), quoteId: x.id })}
            onRevise={(x) => setEditor({ draft: quoteDraft(x), replacesId: x.id })} />
        ))}
      </ul>
      {editor && <QuoteEditor siteId={siteId} editor={editor} onClose={() => setEditor(null)} onSaved={reload} />}
      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Eliminare il preventivo?" description={del?.title} onConfirm={remove} />
    </section>
  );
}
